/**
 * CustomerDashboard.jsx
 * Main dashboard home for wedding customers
 * Design: Calming, organized, premium feel
 * Shows progress, quick actions, and reassuring messaging
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Heart,
  UtensilsCrossed,
  BedDouble,
  CalendarDays,
  Mail,
  Palette,
  ChevronRight,
  CheckCircle2,
  Clock,
  Loader2,
  Phone,
  Sparkles,
  Calendar,
  MapPin
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import CustomerLayout from "./CustomerLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/customer`;

// Module configuration with icons and colors
const MODULE_CONFIG = {
  food_menu: {
    icon: UtensilsCrossed,
    color: "from-[#E8B4B8] to-[#D4A5A5]",
    bgColor: "bg-[#FDF2F2]",
    path: "/my-wedding/food-menu"
  },
  room_allocation: {
    icon: BedDouble,
    color: "from-[#A8C5DA] to-[#8FB3C9]",
    bgColor: "bg-[#F2F7FA]",
    path: "/my-wedding/rooms"
  },
  itinerary: {
    icon: CalendarDays,
    color: "from-[#C5D5C5] to-[#A8C5A8]",
    bgColor: "bg-[#F5FAF5]",
    path: "/my-wedding/itinerary"
  },
  invite_page: {
    icon: Mail,
    color: "from-[#D4C5E0] to-[#C0B0D0]",
    bgColor: "bg-[#F8F5FA]",
    path: "/my-wedding/invite"
  },
  decor: {
    icon: Palette,
    color: "from-[#E8D5B8] to-[#D4C0A0]",
    bgColor: "bg-[#FAF7F2]",
    path: "/my-wedding/decor"
  }
};

// Status configuration
const STATUS_CONFIG = {
  not_started: { label: "Not Started", color: "text-gray-400", icon: Clock },
  in_progress: { label: "In Progress", color: "text-amber-500", icon: Clock },
  submitted: { label: "Submitted", color: "text-blue-500", icon: CheckCircle2 },
  under_review: { label: "Under Review", color: "text-purple-500", icon: Clock },
  finalized: { label: "Finalized", color: "text-green-600", icon: CheckCircle2 }
};

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      const token = localStorage.getItem("customer_token");
      if (!token) {
        navigate("/my-wedding");
        return;
      }

      try {
        const response = await axios.get(`${API}/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDashboardData(response.data);
      } catch (error) {
        if (error.response?.status === 401) {
          localStorage.removeItem("customer_token");
          navigate("/my-wedding");
          toast.error("Session expired. Please login again.");
        } else {
          toast.error("Unable to load dashboard. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FDF8F5] via-[#FAF5F2] to-[#F5EDE8] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-[#C9A87C] mx-auto mb-4" />
          <p className="text-[#8B7355]">Preparing your wedding dashboard...</p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return null;
  }

  const { customer, wedding, content, modules_enabled, module_statuses, progress } = dashboardData;

  return (
    <CustomerLayout customer={customer} wedding={wedding}>
      <div className="space-y-8">
        {/* Welcome Section */}
        <section className="text-center py-6 sm:py-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C9A87C]/10 rounded-full mb-4">
            <Sparkles className="w-4 h-4 text-[#C9A87C]" />
            <span className="text-sm font-medium text-[#8B6914]">
              {wedding?.couple_names || "Your Wedding"}
            </span>
          </div>
          
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-[#5C4A3D] mb-3">
            {content.welcome_title || "Welcome to Your Wedding Planning Hub"}
          </h1>
          <p className="text-[#8B7355] max-w-2xl mx-auto text-sm sm:text-base">
            {content.welcome_subtitle || "Everything you need for your special day, beautifully organized in one place."}
          </p>
        </section>

        {/* Wedding Info Cards */}
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-5 border border-[#E8DED5] flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#C9A87C]/10 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-[#C9A87C]" />
            </div>
            <div>
              <p className="text-xs text-[#8B7355] uppercase tracking-wide">Wedding Date</p>
              <p className="font-medium text-[#5C4A3D]">
                {wedding?.wedding_date || "To be confirmed"}
              </p>
            </div>
          </div>
          
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-5 border border-[#E8DED5] flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#C9A87C]/10 flex items-center justify-center">
              <MapPin className="w-6 h-6 text-[#C9A87C]" />
            </div>
            <div>
              <p className="text-xs text-[#8B7355] uppercase tracking-wide">Venue</p>
              <p className="font-medium text-[#5C4A3D]">
                {wedding?.venue_name || "KAI by Happy Retreats"}
              </p>
            </div>
          </div>
          
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-5 border border-[#E8DED5] flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#C9A87C]/10 flex items-center justify-center">
              <Heart className="w-6 h-6 text-[#C9A87C]" />
            </div>
            <div>
              <p className="text-xs text-[#8B7355] uppercase tracking-wide">Package</p>
              <p className="font-medium text-[#5C4A3D]">
                {wedding?.package_type || "Standard"}
              </p>
            </div>
          </div>
        </section>

        {/* Progress Section */}
        <section className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-[#E8DED5]">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <div>
              <h2 className="text-lg font-semibold text-[#5C4A3D]">
                {content.progress_title || "Your Planning Progress"}
              </h2>
              <p className="text-sm text-[#8B7355]">
                {content.progress_subtitle || "Track your wedding preparations at a glance"}
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-[#C9A87C]">{progress.percent}%</p>
              <p className="text-xs text-[#8B7355]">{progress.completed} of {progress.total} completed</p>
            </div>
          </div>
          <Progress 
            value={progress.percent} 
            className="h-3 bg-[#F5EDE8]"
          />
        </section>

        {/* Reassuring Message */}
        <section className="bg-gradient-to-r from-[#C9A87C]/10 to-[#D4B896]/10 rounded-2xl p-6 border border-[#D4C5B0]/30">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
              <Heart className="w-5 h-5 text-[#C9A87C]" />
            </div>
            <div>
              <p className="text-[#5C4A3D] font-medium mb-1">You're in safe hands</p>
              <p className="text-sm text-[#8B7355]">
                {content.welcome_message || "Our team at Happy Retreats is here to make your celebration unforgettable. Let's bring your vision to life together."}
              </p>
            </div>
          </div>
        </section>

        {/* Module Cards */}
        <section>
          <h2 className="text-lg font-semibold text-[#5C4A3D] mb-4">
            Your Planning Modules
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {modules_enabled.map((moduleKey) => {
              const config = MODULE_CONFIG[moduleKey];
              const status = module_statuses[moduleKey] || { status: "not_started" };
              const statusConfig = STATUS_CONFIG[status.status] || STATUS_CONFIG.not_started;
              const StatusIcon = statusConfig.icon;
              const ModuleIcon = config?.icon || Heart;

              if (!config) return null;

              return (
                <button
                  key={moduleKey}
                  onClick={() => navigate(config.path)}
                  className="group bg-white/70 backdrop-blur-sm rounded-2xl p-5 border border-[#E8DED5] hover:border-[#C9A87C]/50 hover:shadow-lg transition-all duration-300 text-left"
                  data-testid={`module-card-${moduleKey}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${config.color} flex items-center justify-center shadow-sm`}>
                      <ModuleIcon className="w-6 h-6 text-white" />
                    </div>
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-full ${config.bgColor}`}>
                      <StatusIcon className={`w-3 h-3 ${statusConfig.color}`} />
                      <span className={`text-xs font-medium ${statusConfig.color}`}>
                        {statusConfig.label}
                      </span>
                    </div>
                  </div>
                  
                  <h3 className="font-semibold text-[#5C4A3D] mb-1 group-hover:text-[#8B6914] transition-colors">
                    {content[`${moduleKey}_title`] || moduleKey.replace(/_/g, " ")}
                  </h3>
                  <p className="text-sm text-[#8B7355] line-clamp-2">
                    {content[`${moduleKey}_description`] || "Plan this aspect of your wedding"}
                  </p>
                  
                  <div className="flex items-center gap-1 mt-4 text-[#C9A87C] text-sm font-medium">
                    <span>{status.status === "not_started" ? "Get Started" : "Continue"}</span>
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Support Section */}
        <section className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-[#E8DED5]">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-center sm:text-left">
              <p className="font-medium text-[#5C4A3D] mb-1">
                {content.support_message || "Need help? Our wedding planning team is just a message away."}
              </p>
              <p className="text-sm text-[#8B7355]">
                We're here to make your planning journey smooth and joyful.
              </p>
            </div>
            <a 
              href={`tel:${content.support_phone || "+919876543210"}`}
              className="flex items-center gap-2 px-5 py-3 bg-[#C9A87C] hover:bg-[#B8976B] text-white rounded-xl font-medium transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span>{content.support_phone || "+91 98765 43210"}</span>
            </a>
          </div>
        </section>
      </div>
    </CustomerLayout>
  );
};

export default CustomerDashboard;
